import { useId } from 'react';
import {
  LIST_OPERATORS,
  RANGE_OPERATORS,
  VALUELESS_OPERATORS,
  groupChildren,
  groupKind,
  isGroup,
  operatorsFor,
  validateRule,
  type Operator,
  type RuleField,
  type RuleNode,
  type RuleSchema,
} from '@whinerp/contracts/rules';
import { cx } from '../primitives/cx.js';
import { Icon } from '../primitives/Icon.tsx';
import { Num } from '../primitives/Num.tsx';
import { IconButton, InlineAction } from '../primitives/Button.tsx';
import { passthrough } from '../primitives/passthrough.js';

/**
 * RuleBuilder - one component, one document, a schema per caller.
 *
 * Six things in this product ask a person to describe a set: a pricelist rule,
 * a promotion, a sharing rule, a segment, an automation trigger, an assignment
 * rule. Their outcomes have nothing in common - a price, visibility, a count,
 * whether to run - and that difference is exactly what makes forking feel
 * justified each time.
 *
 * What they share is the question: fields, operators, values, grouped by
 * all/any/none, nested to the compiler's depth. Six builders would mean five
 * of them missing nested groups and three disagreeing about what "is empty"
 * means for a number - and the one a customer reports a bug in would be the
 * one nobody else had fixed.
 *
 * So the differences are a `RuleSchema`: which fields may be asked about, how
 * deep nesting may go, and what a match produces. If a caller genuinely needs
 * something else, it extends the schema. It does not fork.
 *
 * Validation is the same function the server runs, so a rule the builder
 * accepted cannot be refused on save - the single most common way a rule
 * builder becomes unusable.
 */

export interface RuleBuilderLabels {
  all: string;
  any: string;
  none: string;
  addCondition: string;
  addGroup: string;
  remove: string;
  field: string;
  operator: string;
  value: string;
  and: string;
  or: string;
  /** How many conditions, and what a match produces. */
  summary: (count: string, outcome: string) => string;
  /** Live, as the person types. */
  matching: (count: string) => string;
  matchingUnknown: string;
  sampleRows: string;
  operatorLabels: Record<Operator, string>;
  tooDeep: (max: string) => string;
}

export interface RuleBuilderProps {
  schema: RuleSchema;
  value: RuleNode;
  onChange: (next: RuleNode) => void;
  labels: RuleBuilderLabels;
  /** Live count of what the rule currently matches. Null while unknown. */
  matchCount?: number | null;
  /** A few rows that match, so the count is checkable rather than believed. */
  sampleRows?: Array<{ id: string; label: string }>;
  readOnly?: boolean;
}

const EMPTY_CONDITION = (schema: RuleSchema): RuleNode => {
  const field = schema.fields[0];
  const operator = (field ? (field.operators ?? operatorsFor(field.type))[0] : 'eq') as Operator;
  return { field: field?.key ?? '', operator, value: '' };
};

export function RuleBuilder({
  schema,
  value,
  onChange,
  labels,
  matchCount,
  sampleRows,
  readOnly,
  ...rest
}: RuleBuilderProps & Record<string, unknown>): JSX.Element {
  const problems = validateRule(value, schema);
  const problemAt = (path: string): string | undefined =>
    problems.find((problem) => problem.path === path)?.message;

  function replaceAt(path: string, next: RuleNode | null): void {
    onChange(replaceNode(value, path, next) ?? { all: [] });
  }

  return (
    <div {...passthrough(rest)} data-component="RuleBuilder" className="flex flex-col gap-3">
      <NodeEditor
        node={value}
        path=""
        depth={1}
        schema={schema}
        labels={labels}
        readOnly={readOnly ?? false}
        problemAt={problemAt}
        onReplace={replaceAt}
      />

      <div className="flex flex-wrap items-center gap-3 text-meta text-ink-meta">
        <Num data-rule-summary>
          {labels.summary(String(countConditionsIn(value)), schema.outcome)}
        </Num>
        {/* The live count. A rule builder without one asks somebody to guess
            whether they have described the set they meant. */}
        <Num data-rule-matches>
          {matchCount === null || matchCount === undefined
            ? labels.matchingUnknown
            : labels.matching(String(matchCount))}
        </Num>
      </div>

      {/* And a few of them, because a count alone is a number somebody has to
          take on trust - and the one thing a rule gets wrong is which rows it
          catches, not how many. */}
      {sampleRows && sampleRows.length > 0 ? (
        <div data-rule-samples className="flex flex-col gap-1">
          <span className="text-micro uppercase tracking-label text-ink-label">
            {labels.sampleRows}
          </span>
          <ul className="flex flex-col">
            {sampleRows.map((row) => (
              <li key={row.id} className="border-t border-t-rule py-1 text-dense text-ink first:border-t-0">
                {row.label}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function NodeEditor({
  node,
  path,
  depth,
  schema,
  labels,
  readOnly,
  problemAt,
  onReplace,
}: {
  node: RuleNode;
  path: string;
  depth: number;
  schema: RuleSchema;
  labels: RuleBuilderLabels;
  readOnly: boolean;
  problemAt: (path: string) => string | undefined;
  onReplace: (path: string, next: RuleNode | null) => void;
}): JSX.Element {
  if (!isGroup(node)) {
    return (
      <ConditionEditor
        condition={node}
        path={path}
        schema={schema}
        labels={labels}
        readOnly={readOnly}
        problem={problemAt(path)}
        onReplace={onReplace}
      />
    );
  }

  const kind = groupKind(node);
  const children = groupChildren(node);
  const canNest = depth < schema.maxDepth;
  const problem = problemAt(path);

  return (
    <div
      data-rule-group={kind}
      data-rule-path={path || 'root'}
      className={cx('flex flex-col gap-2 rounded-card border border-rule p-2', depth > 1 && 'bg-sunken')}
    >
      <div className="flex flex-wrap items-center gap-2">
        {/* The joiner is a control, not a label: changing all to any is the
            single most common edit and must not mean rebuilding the group. */}
        <label className="flex items-center gap-1 text-meta text-ink-meta">
          <span className="sr-only">{labels.and}</span>
          <select
            data-group-kind={path || 'root'}
            aria-label={labels.and}
            value={kind}
            disabled={readOnly}
            onChange={(event) => onReplace(path, retype(node, event.target.value as 'all' | 'any' | 'none'))}
            className="h-input rounded-control border border-rule bg-surface px-2 text-meta text-ink"
          >
            <option value="all">{labels.all}</option>
            <option value="any">{labels.any}</option>
            <option value="none">{labels.none}</option>
          </select>
        </label>

        {!readOnly ? (
          <>
            <InlineAction
              data-add-condition={path || 'root'}
              onClick={() => onReplace(path, withChildren(node, [...children, EMPTY_CONDITION(schema)]))}
            >
              {labels.addCondition}
            </InlineAction>
            {canNest ? (
              <InlineAction
                data-add-group={path || 'root'}
                onClick={() =>
                  onReplace(path, withChildren(node, [...children, { all: [EMPTY_CONDITION(schema)] }]))
                }
              >
                {labels.addGroup}
              </InlineAction>
            ) : (
              // Said, rather than the control quietly not being there. A
              // builder that stops offering something without saying why reads
              // as broken.
              <span data-max-depth className="text-meta text-ink-meta">
                {labels.tooDeep(String(schema.maxDepth))}
              </span>
            )}
          </>
        ) : null}
      </div>

      {problem ? (
        <p role="alert" className="flex items-center gap-1 text-meta text-danger-text">
          <Icon name="Warning" size="inline" decorative />
          {problem}
        </p>
      ) : null}

      <ol className="flex flex-col gap-2">
        {children.map((child, index) => {
          const childPath = path === '' ? `${kind}.${String(index)}` : `${path}.${kind}.${String(index)}`;
          return (
            <li key={childPath} className="flex items-start gap-2">
              <NodeEditor
                node={child}
                path={childPath}
                depth={depth + 1}
                schema={schema}
                labels={labels}
                readOnly={readOnly}
                problemAt={problemAt}
                onReplace={onReplace}
              />
              {!readOnly ? (
                <IconButton
                  icon="X"
                  label={labels.remove}
                  data-remove-node={childPath}
                  onClick={() =>
                    onReplace(path, withChildren(node, children.filter((_, i) => i !== index)))
                  }
                />
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ConditionEditor({
  condition,
  path,
  schema,
  labels,
  readOnly,
  problem,
  onReplace,
}: {
  condition: Extract<RuleNode, { field: string }>;
  path: string;
  schema: RuleSchema;
  labels: RuleBuilderLabels;
  readOnly: boolean;
  problem: string | undefined;
  onReplace: (path: string, next: RuleNode | null) => void;
}): JSX.Element {
  const id = useId();
  const field: RuleField | undefined = schema.fields.find((entry) => entry.key === condition.field);
  const operators = field ? (field.operators ?? operatorsFor(field.type)) : [];
  const takesNoValue = VALUELESS_OPERATORS.includes(condition.operator);
  const takesList = LIST_OPERATORS.includes(condition.operator);
  const takesRange = RANGE_OPERATORS.includes(condition.operator);

  return (
    <div
      data-rule-condition={path}
      className="flex min-w-0 flex-1 flex-wrap items-center gap-2 rounded-control bg-surface p-1"
    >
      <label className="flex min-w-0 items-center gap-1">
        <span className="sr-only">{labels.field}</span>
        <select
          id={`${id}-field`}
          data-condition-field={path}
          aria-label={labels.field}
          value={condition.field}
          disabled={readOnly}
          onChange={(event) => {
            // Changing the field can invalidate the operator, so the operator
            // moves with it rather than being left pointing at something the
            // new type cannot do.
            const next = schema.fields.find((entry) => entry.key === event.target.value);
            const allowed = next ? (next.operators ?? operatorsFor(next.type)) : [];
            const operator = allowed.includes(condition.operator)
              ? condition.operator
              : (allowed[0] ?? 'eq');
            onReplace(path, { field: event.target.value, operator, value: '' });
          }}
          className="h-input min-w-0 rounded-control border border-rule bg-surface px-2 text-dense text-ink"
        >
          {schema.fields.map((entry) => (
            <option key={entry.key} value={entry.key}>
              {entry.label}
            </option>
          ))}
        </select>
      </label>

      <label className="flex min-w-0 items-center gap-1">
        <span className="sr-only">{labels.operator}</span>
        <select
          data-condition-operator={path}
          aria-label={labels.operator}
          value={condition.operator}
          disabled={readOnly}
          onChange={(event) => {
            const operator = event.target.value as Operator;
            // The value shape changes with the operator: a list operator needs
            // values, a valueless one needs neither. Carrying the old shape
            // over is how a condition ends up invalid without being edited.
            const base = { field: condition.field, operator };
            if (VALUELESS_OPERATORS.includes(operator)) onReplace(path, base);
            else if (LIST_OPERATORS.includes(operator) || RANGE_OPERATORS.includes(operator)) {
              onReplace(path, { ...base, values: [] });
            } else onReplace(path, { ...base, value: '' });
          }}
          className="h-input min-w-0 rounded-control border border-rule bg-surface px-2 text-dense text-ink"
        >
          {operators.map((operator) => (
            <option key={operator} value={operator}>
              {labels.operatorLabels[operator]}
            </option>
          ))}
        </select>
      </label>

      {takesNoValue ? null : takesRange ? (
        <span className="flex items-center gap-1">
          {[0, 1].map((index) => (
            <input
              key={index}
              data-condition-value={`${path}.${String(index)}`}
              aria-label={`${labels.value} ${String(index + 1)}`}
              value={String(condition.values?.[index] ?? '')}
              disabled={readOnly}
              onChange={(event) => {
                const values = [...(condition.values ?? ['', ''])];
                values[index] = event.target.value;
                onReplace(path, { ...condition, values });
              }}
              className="h-input w-block-sm rounded-control border border-rule bg-surface px-2 text-dense text-ink"
            />
          ))}
        </span>
      ) : takesList ? (
        <input
          data-condition-value={path}
          aria-label={labels.value}
          value={(condition.values ?? []).join(', ')}
          disabled={readOnly}
          onChange={(event) =>
            onReplace(path, {
              ...condition,
              values: event.target.value
                .split(',')
                .map((entry) => entry.trim())
                .filter(Boolean),
            })
          }
          className="h-input min-w-0 flex-1 rounded-control border border-rule bg-surface px-2 text-dense text-ink"
        />
      ) : field?.options?.length ? (
        <select
          data-condition-value={path}
          aria-label={labels.value}
          value={String(condition.value ?? '')}
          disabled={readOnly}
          onChange={(event) => onReplace(path, { ...condition, value: event.target.value })}
          className="h-input min-w-0 rounded-control border border-rule bg-surface px-2 text-dense text-ink"
        >
          <option value="">{labels.value}</option>
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          data-condition-value={path}
          aria-label={labels.value}
          value={String(condition.value ?? '')}
          disabled={readOnly}
          onChange={(event) => onReplace(path, { ...condition, value: event.target.value })}
          className="h-input min-w-0 flex-1 rounded-control border border-rule bg-surface px-2 text-dense text-ink"
        />
      )}

      {problem ? (
        <span role="alert" data-condition-problem={path} className="text-meta text-danger-text">
          {problem}
        </span>
      ) : null}
    </div>
  );
}

// --- document surgery ------------------------------------------------------

function withChildren(group: RuleNode, children: RuleNode[]): RuleNode {
  const kind = groupKind(group as Parameters<typeof groupKind>[0]);
  if (kind === 'all') return { all: children };
  if (kind === 'any') return { any: children };
  return { none: children };
}

function retype(group: RuleNode, kind: 'all' | 'any' | 'none'): RuleNode {
  const children = groupChildren(group as Parameters<typeof groupChildren>[0]);
  if (kind === 'all') return { all: children };
  if (kind === 'any') return { any: children };
  return { none: children };
}

/** Replaces the node at `path`, or removes it when `next` is null. */
export function replaceNode(root: RuleNode, path: string, next: RuleNode | null): RuleNode | null {
  if (path === '') return next;
  const [head, index, ...tail] = path.split('.');
  if (!head || index === undefined || !isGroup(root)) return root;

  const children = [...groupChildren(root)];
  const position = Number(index);
  const child = children[position];
  if (!child) return root;

  if (tail.length === 0) {
    if (next === null) children.splice(position, 1);
    else children[position] = next;
  } else {
    const replaced = replaceNode(child, tail.join('.'), next);
    if (replaced === null) children.splice(position, 1);
    else children[position] = replaced;
  }
  return withChildren(root, children);
}

function countConditionsIn(node: RuleNode): number {
  if (!isGroup(node)) return 1;
  return groupChildren(node).reduce((total, child) => total + countConditionsIn(child), 0);
}

